/**
 * PMC Programme Detail lookups/table calls with React Query.
 * Use QUERY_KEYS.programDetail.root to invalidate everything under this domain.
 */
import {
    GetCampusBuildingsList,
} from '../../services/CampusBuildingService';
import { GetNetsuiteProductListCancelToken } from '../../services/ProgrammeServices';
import {
    GetPagesList,
    GetSchoolList,
    getPagesListForPmcFilters,
    getProgrammeSummary,
    getVersionSummary,
} from '../../services/ProgramService';
import { modifiyOptionsForSelect } from '../../utils/commonFunction';
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from '../queryConsts';
import { useBaseReactQuery } from './useBaseReactQuery';

const EMPTY_PAGE_FILTERS = Object.freeze({ status: [], BrandList: [] });
const EMPTY_ASSIGN_PAGE_LIST = Object.freeze({ data: [], total: 0 });
const EMPTY_PROGRAMME_SUMMARY = Object.freeze({
    programmes: 0,
    modules: 0,
    students: 0,
    graduates: 0,
    lockedProgrammes: 0,
});

/**
 * Schools dropdown - rarely changes - PERSISTED
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useSchoolList() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.schoolList],
        async () => {
            const res = await GetSchoolList();
            return res.data.data ?? [];
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP,
        }
    );

    return {
        ...query,
        data: query.data ?? [],
    };
}

/**
 * Netsuite product dropdown options - rarely changes - PERSISTED
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useNetsuiteProductDropdown() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.netsuiteProductDropdown],
        async () => {
            const res = await GetNetsuiteProductListCancelToken("");
            const items = res.data.data?.items ?? [];
            return modifiyOptionsForSelect(items, "netsuite");
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP,
        }
    );

    return {
        ...query,
        data: query.data ?? [],
    };
}

/**
 * Status/brand template filter options for the PMC "Assign Page" table - PERSISTED
 * @returns {DefinedUseQueryResult} React Query result
 */
export function usePageFiltersForPmc() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.pageFiltersForPmc],
        async () => {
            const res = await getPagesListForPmcFilters();
            return {
                BrandList: res.data?.getBrandtemplate ?? [],
                status: res.data?.getStatus ?? [],
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP,
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_PAGE_FILTERS,
    };
}

/**
 * PMC "Assign Page" table - paginated/searched/filtered - NOT PERSISTED
 * @param {object} requestData Table payload (page, perPage, search, key, sort, exportStatus, viaBrand, viaStatus).
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useAssignPageList(requestData) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.assignPageList, requestData],
        async () => {
            const res = await GetPagesList(requestData);
            return {
                data: res.data?.Data?.data ?? [],
                total: res.data?.Data?.total ?? 0,
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE,
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_ASSIGN_PAGE_LIST,
    };
}

/**
 * Campus buildings dropdown - rarely changes - PERSISTED
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useCampusBuildingsList() {
    const query = useBaseReactQuery(
        [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.campusBuildings],
        async () => {
            const res = await GetCampusBuildingsList();
            return res.data?.campusBuildingDetails ?? [];
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP,
        }
    );

    return {
        ...query,
        data: query.data ?? [],
    };
}

/**
 * PMC-wide summary totals for the Version Summary Strip - NOT PERSISTED
 * @param {string | number} pmcId Programme id.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useProgrammeSummary(pmcId) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.programmeSummary, pmcId],
        async () => {
            const res = await getProgrammeSummary(pmcId);
            return res?.data?.data ?? EMPTY_PROGRAMME_SUMMARY;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: Boolean(pmcId),
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_PROGRAMME_SUMMARY,
    };
}

/**
 * Summary totals scoped to one specific PMC version, for the Version
 * Details/Modules screens' Version Summary Strip - NOT PERSISTED
 * @param {string | number} pmcId Programme id.
 * @param {string | number} versionId Version id to scope totals to.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useVersionSummary(pmcId, versionId) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.versionSummary, pmcId, versionId],
        async () => {
            const res = await getVersionSummary(pmcId, versionId);
            return res?.data?.data ?? EMPTY_PROGRAMME_SUMMARY;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: Boolean(pmcId && versionId),
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_PROGRAMME_SUMMARY,
    };
}
